/**
 * Port of {@class FieldPath} —
 * backend/src/main/java/com/irian/iam/ruleengine/api/FieldPath.java
 *
 * Typed representation of a v2 field-path expression.
 */

import * as FieldPaths from './FieldPaths'

export type FieldPathSegment = {
    readonly name: string
    readonly rowReferenceLiteral: string | null
    readonly typeFilterValue: string | null
}

export const COUNT_NAME = 'count'

export function segment(name: string, rowReferenceLiteral: string | null = null, typeFilterValue: string | null = null): FieldPathSegment {
    return {name, rowReferenceLiteral, typeFilterValue}
}

export function hasRowReference(s: FieldPathSegment): boolean {
    return s.rowReferenceLiteral !== null
}

export function hasTypeFilter(s: FieldPathSegment): boolean {
    return s.typeFilterValue !== null
}

export function isCount(s: FieldPathSegment): boolean {
    return s.name === COUNT_NAME
}

export function segmentToString(s: FieldPathSegment): string {
    let result = s.name
    if (s.rowReferenceLiteral !== null) result += `[${s.rowReferenceLiteral}]`
    if (s.typeFilterValue !== null) result += `(type='${s.typeFilterValue}')`
    return result
}

export type FieldPath = {
    readonly segments: readonly FieldPathSegment[]
}

export function fieldPath(segments: readonly FieldPathSegment[]): FieldPath {
    if (segments === null || segments === undefined || segments.length === 0) {
        throw new Error('FieldPath must have at least one segment')
    }
    return {segments: [...segments]}
}

/**
 * Parse a raw path string into a typed FieldPath.
 */
export function parseFieldPath(raw: string): FieldPath {
    if (raw === null || raw === undefined || raw === '') {
        throw new Error('Cannot parse empty field path')
    }
    const rawSegments = FieldPaths.splitFieldPath(raw)
    const parsed: FieldPathSegment[] = []
    for (const seg of rawSegments) {
        parsed.push(parseSegment(seg))
    }
    return fieldPath(parsed)
}

function parseSegment(raw: string): FieldPathSegment {
    const name = FieldPaths.stripTypeFilter(FieldPaths.stripRowReference(raw))
    const rowRef = FieldPaths.hasRowReference(raw) ? FieldPaths.extractRowReferenceLiteral(raw) : null
    const typeFilter = FieldPaths.extractTypeFilterValue(raw)
    return segment(name, rowRef, typeFilter)
}

export function root(p: FieldPath): FieldPathSegment {
    return p.segments[0]
}

export function terminal(p: FieldPath): FieldPathSegment {
    return p.segments[p.segments.length - 1]
}

export function rootName(p: FieldPath): string {
    return root(p).name
}

export function depth(p: FieldPath): number {
    return p.segments.length
}

export function endsWithCount(p: FieldPath): boolean {
    return p.segments.length >= 2 && isCount(terminal(p))
}

export function withoutCountTerminal(p: FieldPath): FieldPath {
    if (!endsWithCount(p)) {
        throw new Error(`withoutCountTerminal called on path not ending with count: ${fieldPathToString(p)}`)
    }
    return fieldPath(p.segments.slice(0, p.segments.length - 1))
}

export function fieldPathToString(p: FieldPath): string {
    return p.segments.map(segmentToString).join('.')
}
